import React, { useCallback, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';

import { useAuth } from '@/providers/AuthProvider';
import {
	useAnalyticsEvent,
	useFireAnalyticsEvent,
} from '@/providers/AnalyticsProvider';
import { useIntercom } from '@/providers/IntercomProvider/IntercomProvider';
import {
	openCheckout,
	useSubscription,
} from '@/providers/SubscriptionProvider';
import { useTeam } from '@/providers/TeamProvider';

import { AccountDisplayProps } from '../../AccountPanel';
import { PricingCard } from './PricingCard';
import styles from './Upgrade.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

// Ordered from lowest to highest tier.
const PLAN_ORDER = ['free', 'leader', 'team'] as const;
type PlanId = (typeof PLAN_ORDER)[number];

interface PlanDef {
	id: PlanId;
	title: string;
	description: string;
	pricePrefix?: string;
	priceAmount: string;
	priceSuffix: string;
	priceNote?: string;
	includes: string;
	baseIncludes?: string;
	features: string[];
	comingSoon?: boolean; // true = always disabled regardless of tier relationship
}

const PLAN_DEFS: PlanDef[] = [
	{
		id: 'free',
		title: 'Free',
		description: 'Start planning worship with AI—free forever',
		priceAmount: '$0',
		priceSuffix: 'forever',
		priceNote: 'No credit card required',
		includes: '1 service',
		features: [
			'Unlimited setlists',
			'Gap analysis',
			'1 admin',
			'Last 30 days of data',
		],
	},
	{
		id: 'leader',
		title: 'Leader',
		description: 'For worship leaders who want unlimited planning',
		priceAmount: '$12',
		priceSuffix: 'USD/mo.',
		priceNote: 'Cancel anytime',
		includes: '1 service',
		baseIncludes: 'Everything in Free, plus',
		features: [
			'Unlimited data history',
			'Gap analysis, plus recommendations',
			'Private songs',
		],
	},
	{
		id: 'team',
		title: 'Team',
		description: 'For multi-service churches who want automated insights',
		pricePrefix: 'Starts at',
		priceAmount: '$39',
		priceSuffix: 'USD/mo.',
		priceNote: '+$10/mo. per additional service',
		includes: '2 services',
		baseIncludes: 'Everything in Leader, plus',
		features: [
			'Automated worship reports',
			'Cross-service analytics',
			'Unlimited admins',
		],
		comingSoon: true,
	},
];

export interface UpgradeProps {
	contentText: string;
	onClose: () => void;
	popDisplayState?: AccountDisplayProps['popDisplayState'];
}

export const Upgrade: React.FC<UpgradeProps> = ({
	onClose,
	popDisplayState,
}) => {
	const { user } = useAuth();
	const { team } = useTeam();
	const { chargebeeInstance, cart, status: subscriptionStatus } = useSubscription();
	const { openIntercom } = useIntercom();

	useFireAnalyticsEvent({ eventName: 'Upgrade_View_Panel' });
	const fireClickLeaderCta = useAnalyticsEvent('Upgrade_Click_Solo_CTA');
	const fireDowngradeClick = useAnalyticsEvent('Upgrade_Click_Downgrade');
	const fireRequestTeamUpgrade = useAnalyticsEvent('Upgrade_Click_Request_Team');

	const handleOpenCheckout = useCallback(() => {
		fireClickLeaderCta();
		openCheckout({
			cbInstance: chargebeeInstance,
			cart,
			user,
			team,
			period: 'Monthly',
			currency: 'USD',
			planPriceIdPrefix: 'Solo',
			onClose,
			popDisplayState,
		});
	}, [
		chargebeeInstance,
		cart,
		user,
		team,
		onClose,
		popDisplayState,
		fireClickLeaderCta,
	]);

	const currentPlanId: PlanId = subscriptionStatus ? 'leader' : 'free';
	const currentTier = PLAN_ORDER.indexOf(currentPlanId);

	const sortedCards = useMemo(() => {
		const getGroup = (tier: number) => {
			if (tier === currentTier) return 2;  // current — last
			if (tier > currentTier) return 0;    // upgrade — first
			return 1;                             // downgrade — middle
		};

		return [...PLAN_DEFS].sort((a, b) => {
			const aTier = PLAN_ORDER.indexOf(a.id);
			const bTier = PLAN_ORDER.indexOf(b.id);
			const aGroup = getGroup(aTier);
			const bGroup = getGroup(bTier);
			if (aGroup !== bGroup) return aGroup - bGroup;
			if (aGroup === 0) return aTier - bTier; // upgrades: nearest first
			if (aGroup === 1) return bTier - aTier; // downgrades: nearest first
			return 0;
		});
	}, [currentTier]);

	const getCardCta = (plan: PlanDef): { label: string; disabled: boolean; noIcon: boolean; color: 'blue' | 'green' | 'grey' | 'inactive' | 'white' | 'gradient'; onClick?: () => void } => {
		if (plan.id === currentPlanId) {
			return { label: 'Your current plan', disabled: true, noIcon: true, color: 'grey' };
		}
		if (plan.comingSoon) {
			return { label: 'Request Upgrade', disabled: false, noIcon: true, color: 'grey', onClick: () => { fireRequestTeamUpgrade(); openIntercom(); } };
		}
		const tier = PLAN_ORDER.indexOf(plan.id);
		if (tier > currentTier) {
			return { label: `Upgrade to ${plan.title}`, disabled: false, noIcon: false, color: 'green', onClick: handleOpenCheckout };
		}
		// downgrade — contact support via Intercom
		return { label: 'Contact to Downgrade', disabled: false, noIcon: true, color: 'grey', onClick: () => { fireDowngradeClick(); openIntercom(); } };
	};

	return (
		<div className={styles['upgrade']}>
			{!isCapacitor && (
				<div>
					{sortedCards.map((plan, index) => {
						const cta = getCardCta(plan);
						return (
							<React.Fragment key={plan.id}>
								{index > 0 && <div className="vsb-20" />}
								<PricingCard
									title={plan.title}
									description={plan.description}
									pricePrefix={plan.pricePrefix}
									priceAmount={plan.priceAmount}
									priceSuffix={plan.priceSuffix}
									priceNote={plan.priceNote}
									includes={plan.includes}
									baseIncludes={plan.baseIncludes}
									features={plan.features}
									buttonLabel={cta.label}
									buttonColor={cta.color}
									current={plan.id === currentPlanId}
									disabled={cta.disabled}
									noIcon={cta.noIcon}
									comingSoon={plan.comingSoon}
									onClick={cta.onClick}
								/>
							</React.Fragment>
						);
					})}
				</div>
			)}
			{isCapacitor && (
				<div className={styles['upgrade__ios-message']}>
					Paid plans are available on the web at app.asaph.io
				</div>
			)}
		</div>
	);
};
